'use client';

import { useEffect, useRef, useState } from 'react';
import type { SignedIn } from '@pubky/passport-client';
import type { PassportElement } from '@pubky/passport-client/element';
import { Button } from '@/atoms/Button/Button';
import {
  getDefaultHttpRelay,
  getPassportUrl,
  getPkarrRelays,
  getTestnet,
  HOMESERVER_CAPABILITIES,
} from '@/config/network';
import { PASSPORT_APP_NAME, PASSPORT_CLIENT_ID, PASSPORT_DEFAULT_INSTANCE } from '@/config/passport';
import { usePassportSession } from '@/hooks/usePassportSession/usePassportSession';
import { cn } from '@/libs/utils/utils';
import { toast } from '@/molecules/Toaster/toast';

const DEFAULT_LABEL = 'Log in';

export interface PassportLoginProps {
  variant?: 'small' | 'large';
  /** Override the universal idle label when needed. */
  label?: string;
  /** Header-sized pill. */
  compact?: boolean;
  /** Full-width pill (phone CTAs, dialog cards). */
  block?: boolean;
  className?: string;
}

/** The published element keeps its provider settings, Ring UI and complete auth lifecycle. */
export function PassportLogin({
  variant = 'small',
  label,
  compact = false,
  block = false,
  className,
}: PassportLoginProps) {
  const host = useRef<HTMLDivElement>(null);
  const { acceptSession, isInitializing } = usePassportSession();
  const accept = useRef(acceptSession);
  // The connected element on the page.
  const [element, setElement] = useState<PassportElement>();
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  const idleLabel = label ?? DEFAULT_LABEL;
  const loaded = element !== undefined;

  useEffect(() => {
    accept.current = acceptSession;
  }, [acceptSession]);
  useEffect(() => {
    let disposed = false;
    let element: PassportElement | undefined;
    const onSession = (event: Event) => {
      void accept.current((event as CustomEvent<SignedIn>).detail);
    };
    void import('@pubky/passport-client/element')
      .then(() => {
        if (disposed || !host.current) return;
        element = document.createElement('pubky-passport');
        element.setAttribute('app-name', PASSPORT_APP_NAME);
        element.setAttribute('client-id', PASSPORT_CLIENT_ID);
        element.setAttribute('sync-group', PASSPORT_CLIENT_ID);
        element.setAttribute('instance', getPassportUrl() || PASSPORT_DEFAULT_INSTANCE);
        element.setAttribute('capabilities', HOMESERVER_CAPABILITIES);
        element.setAttribute('profile', 'required');
        element.setAttribute('variant', variant);
        element.setAttribute('network', getTestnet() ? 'testnet' : 'mainnet');
        element.setAttribute('pkarr-relays', getPkarrRelays().join(','));
        element.setAttribute('http-relay', getDefaultHttpRelay());
        // Passport chooses the identity or method; Pubky supplies only the universal button label.
        element.messages = { 'label.idle': idleLabel };
        element.addEventListener('passport-session', onSession);
        host.current.append(element);
        setElement(element);
        setFailed(false);
      })
      .catch(() => {
        if (disposed) return;
        setFailed(true);
        toast({ variant: 'error', description: 'Could not load login. Try again.' });
      });
    return () => {
      disposed = true;
      element?.removeEventListener('passport-session', onSession);
      element?.remove();
      setElement(undefined);
    };
  }, [variant, idleLabel, retry]);

  return (
    <div
      className={cn(
        'passport-login',
        compact && 'passport-login--compact',
        block && 'passport-login--block',
        className,
      )}
      data-testid="passport-login"
      aria-busy={!loaded || isInitializing}
    >
      {!loaded && (
        <Button
          variant="brand"
          size={compact ? 'default' : 'lg'}
          className={cn(block && 'w-full')}
          disabled={!failed}
          onClick={() => setRetry(retry + 1)}
        >
          {failed ? 'Retry login' : idleLabel}
        </Button>
      )}
      <div ref={host} />
      {isInitializing && (
        <p role="status" className="mt-3 text-sm text-muted-foreground">
          {'Loading your Pubky experience…'}
        </p>
      )}
    </div>
  );
}
